import React, { useState, useEffect } from 'react';
import { getStores, getAutoBreakEvenAnalysis } from '../api/client';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';

const MONTHS = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' }, { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BreakEvenAnalysis() {
  const now = new Date();
  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState({
    store_id: '',
    period_month: now.getMonth() + 1,
    period_year: now.getFullYear(),
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getStores().then(setStores).catch(() => {});
  }, []);

  const handleCalculate = async () => {
    if (!filters.store_id) { setError('Selecciona una tienda'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await getAutoBreakEvenAnalysis(filters.store_id, filters.period_month, filters.period_year);
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error calculando el punto de equilibrio');
    } finally {
      setLoading(false);
    }
  };

  const monthLabel = MONTHS.find(m => m.value === filters.period_month)?.label;

  // Bar chart data
  const barData = result ? [
    { name: 'Ventas Reales', value: result.total_revenue, color: result.is_above_break_even ? '#10b981' : '#f59e0b' },
    { name: 'Punto de Equilibrio', value: result.break_even_revenue, color: '#6366f1' },
  ] : [];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📊 Punto de Equilibrio</h1>
        <p className="text-gray-500 mt-1">
          Calcula automáticamente cuánto debe vender una tienda para cubrir todos sus costos — sin ganar ni perder.
        </p>
      </div>

      {/* Selector */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="form-label">Tienda</label>
            <select
              className="form-input"
              value={filters.store_id}
              onChange={e => setFilters(f => ({ ...f, store_id: e.target.value }))}
            >
              <option value="">Seleccionar tienda</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Mes</label>
            <select
              className="form-input"
              value={filters.period_month}
              onChange={e => setFilters(f => ({ ...f, period_month: parseInt(e.target.value) }))}
            >
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Año</label>
            <select
              className="form-input"
              value={filters.period_year}
              onChange={e => setFilters(f => ({ ...f, period_year: parseInt(e.target.value) }))}
            >
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button
            onClick={handleCalculate}
            disabled={loading}
            className="btn-primary h-10"
          >
            {loading ? 'Calculando...' : '🧮 Calcular'}
          </button>
        </div>
        {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
      </div>

      {/* How it works */}
      {!result && !loading && (
        <div className="card bg-blue-50 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">¿Cómo funciona?</h3>
          <p className="text-blue-800 text-sm mb-3">
            El sistema usa los datos que ya tienes registrados para calcular el PE automáticamente:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-white rounded p-3">
              <div className="font-medium text-gray-900 mb-1">💰 Ventas Reales</div>
              <div className="text-gray-600">Total de ventas registradas en el módulo de Ventas para el período seleccionado.</div>
            </div>
            <div className="bg-white rounded p-3">
              <div className="font-medium text-gray-900 mb-1">📦 Costos Variables</div>
              <div className="text-gray-600">Costo de los productos vendidos (COGS) registrado en cada venta.</div>
            </div>
            <div className="bg-white rounded p-3">
              <div className="font-medium text-gray-900 mb-1">🏢 Costos Fijos</div>
              <div className="text-gray-600">Gastos registrados en el módulo de Gastos (nómina, servicios, alquiler, etc.) del período.</div>
            </div>
          </div>
          <div className="mt-3 p-3 bg-white rounded text-sm text-gray-700">
            <strong>Fórmula:</strong> PE = Costos Fijos ÷ (1 − Costos Variables / Ventas)
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Status banner */}
          <div className={`rounded-xl p-5 mb-6 ${result.is_above_break_even ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className={`text-lg font-bold ${result.is_above_break_even ? 'text-green-800' : 'text-red-800'}`}>
                  {result.is_above_break_even ? '✅ Por encima del punto de equilibrio' : '⚠️ Por debajo del punto de equilibrio'}
                </div>
                <div className={`text-sm mt-1 ${result.is_above_break_even ? 'text-green-700' : 'text-red-700'}`}>
                  {result.store_name} · {monthLabel} {result.period_year}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${result.is_above_break_even ? 'text-green-700' : 'text-red-700'}`}>
                  {result.surplus_deficit >= 0 ? '+' : ''}{fmt(result.surplus_deficit)}
                </div>
                <div className="text-sm text-gray-500">vs punto de equilibrio</div>
              </div>
            </div>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card text-center">
              <div className="text-xs text-gray-500 mb-1">Ventas Reales</div>
              <div className="text-xl font-bold text-gray-900">{fmt(result.total_revenue)}</div>
              <div className="text-xs text-gray-400 mt-1">{result.sales_count} días registrados</div>
            </div>
            <div className="card text-center">
              <div className="text-xs text-gray-500 mb-1">Punto de Equilibrio</div>
              <div className="text-xl font-bold text-indigo-700">{fmt(result.break_even_revenue)}</div>
              <div className="text-xs text-gray-400 mt-1">lo mínimo para empatar</div>
            </div>
            <div className="card text-center">
              <div className="text-xs text-gray-500 mb-1">Margen de Contribución</div>
              <div className="text-xl font-bold text-blue-700">{result.margin_contribution_ratio.toFixed(1)}%</div>
              <div className="text-xs text-gray-400 mt-1">de cada $ vendido cubre costos fijos</div>
            </div>
            <div className={`card text-center ${result.safety_margin_percentage >= 0 ? '' : 'bg-red-50'}`}>
              <div className="text-xs text-gray-500 mb-1">Margen de Seguridad</div>
              <div className={`text-xl font-bold ${result.safety_margin_percentage >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {result.safety_margin_percentage.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-400 mt-1">cuánto pueden caer las ventas</div>
            </div>
          </div>

          {/* Cost breakdown + Chart */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Cost structure */}
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">Estructura de Costos</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">📦 Costo de Ventas (Variable)</span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{fmt(result.total_cogs)}</div>
                    <div className="text-xs text-gray-400">{result.variable_cost_ratio.toFixed(1)}% de las ventas</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">🏢 Gastos Fijos</span>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{fmt(result.fixed_costs)}</div>
                    <div className="text-xs text-gray-400">nómina, servicios, etc.</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm font-medium text-gray-700">Total Costos</span>
                  <span className="font-bold text-gray-900">{fmt(result.total_cogs + result.fixed_costs)}</span>
                </div>
              </div>

              {/* Fixed costs by category */}
              {Object.keys(result.costs_by_category).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">Detalle de Gastos Fijos</div>
                  <div className="space-y-1">
                    {Object.entries(result.costs_by_category)
                      .sort(([, a], [, b]) => b - a)
                      .map(([cat, amount]) => (
                        <div key={cat} className="flex justify-between text-xs text-gray-600">
                          <span>{cat}</span>
                          <span className="font-medium">{fmt(amount)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>

            {/* Bar chart */}
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">Ventas vs Punto de Equilibrio</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                  {result.total_revenue > 0 && (
                    <ReferenceLine
                      y={result.break_even_revenue}
                      stroke="#6366f1"
                      strokeDasharray="6 3"
                      label={{ value: 'PE', position: 'right', fontSize: 11, fill: '#6366f1' }}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>

              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>$0</span>
                  <span>PE: {fmt(result.break_even_revenue)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all ${result.is_above_break_even ? 'bg-green-500' : 'bg-red-400'}`}
                    style={{ width: `${Math.min((result.total_revenue / Math.max(result.break_even_revenue, result.total_revenue)) * 100, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-center mt-1 text-gray-500">
                  {result.break_even_revenue > 0
                    ? `${((result.total_revenue / result.break_even_revenue) * 100).toFixed(1)}% del punto de equilibrio alcanzado`
                    : 'Sin costos fijos registrados'}
                </div>
              </div>
            </div>
          </div>

          {/* Explanation */}
          <div className="card bg-gray-50 text-sm text-gray-600">
            <strong className="text-gray-800">¿Cómo se calculó?</strong>
            <div className="mt-2 space-y-1">
              <div>1. Margen de Contribución = 1 − ({fmt(result.total_cogs)} ÷ {fmt(result.total_revenue)}) = <strong>{result.margin_contribution_ratio.toFixed(2)}%</strong></div>
              <div>2. Punto de Equilibrio = {fmt(result.fixed_costs)} ÷ {(result.margin_contribution_ratio / 100).toFixed(4)} = <strong>{fmt(result.break_even_revenue)}</strong></div>
              <div className="text-xs text-gray-400 mt-2">
                * Costos variables = costo de lo vendido (COGS) de las ventas registradas. Costos fijos = gastos del módulo de Gastos (excluye compras de inventario).
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
